import { isDevelopment } from "@cede/utils";
import { clientConfig } from "@/lib/config/client-config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

type AuthResponse = {
  success?: boolean;
  error?: string;
  isAuthenticated?: boolean;
};

export const useDevAuth = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: authStatus, isLoading } = useQuery({
    queryKey: ["dev-auth"],
    queryFn: async (): Promise<AuthResponse> => {
      if (!isDevelopment(clientConfig.nodeEnv)) {
        return { isAuthenticated: true };
      }

      const response = await fetch("/api/dev-auth");
      return response.json();
    },
    retry: false,
  });

  const { mutateAsync: verifyDevPassword, isPending: isVerifying } = useMutation({
    mutationFn: async (password: string): Promise<AuthResponse> => {
      const response = await fetch("/api/dev-auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error("Invalid password");
      }

      return response.json();
    },
    onSuccess: async () => {
      enqueueSnackbar("Authentication successful", { variant: "success" });

      await queryClient.invalidateQueries({ queryKey: ["dev-auth"] });
    },
    onError: (error) => {
      enqueueSnackbar(error instanceof Error ? error.message : "Authentication failed", {
        variant: "error",
      });
    },
  });

  const handleVerifyPassword = async (password: string): Promise<void> => {
    await verifyDevPassword(password);
  };

  return {
    isDevAuthRequired:
      isDevelopment(clientConfig.nodeEnv) && clientConfig.nodeEnv === undefined && !authStatus?.isAuthenticated,
    isDevAuthPassed:
      !isDevelopment(clientConfig.nodeEnv) || clientConfig.nodeEnv !== undefined || authStatus?.isAuthenticated,
    isLoading,
    verifyDevPassword: handleVerifyPassword,
    isVerifying,
  };
};
